/**
 * Mediciones del tanque de un producto (Gestión → Productos).
 *
 * En el panel anterior la tarjeta del combustible sólo mostraba la última
 * medición (`MEDICIÓN: <fecha>`) y el módulo del RIC vivía en otra pantalla, así
 * que para ver cómo se había movido el tanque había que irse al reporte. Aquí se
 * abre esta ventana desde la propia tarjeta, con las últimas tomas del tanque,
 * su físico dibujado frente a la capacidad máxima y el detalle del RIC
 * (anterior, ingresos, salidas, lógico, físico, diferencia, varilla y agua).
 */

import { History, Info, Scale } from 'lucide-react';

import { BarChart } from '@/components/charts/bar-chart';
import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';

import { useMedicionesProducto } from '../productos.api';

/** Fecha corta (`d/m`), o un guion. */
function fechaCorta(valor: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit' });
}

/** Fecha y hora del panel anterior (`d/m/Y H:i`), o un guion. */
function fechaCompleta(valor: string | null): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Número con separador de miles. */
function numero(valor: number | null): string {
  if (valor === null || valor === undefined) return '—';

  return valor.toLocaleString('es-PE', { maximumFractionDigits: 2 });
}

interface Props {
  productoId: number | null;
  /** Nombre del producto, para el título (el listado ya lo tiene). */
  nombre: string;
  onClose: () => void;
}

export function ProductoMedicionesDialog({ productoId, nombre, onClose }: Props) {
  const consulta = useMedicionesProducto(productoId, 12);
  const datos = consulta.data;
  const producto = datos?.producto;
  // El gráfico se dibuja de la más antigua a la más reciente y **sólo** con las
  // tomas vigentes: las anuladas quedan en el detalle, marcadas.
  const mediciones = [...(datos?.mediciones ?? [])].reverse();
  const vigentes = mediciones.filter((medicion) => medicion.vigente);
  const maximo = producto?.capacidad_maxima_tanque ?? null;

  return (
    <Dialog
      open={productoId !== null}
      onClose={onClose}
      size="lg"
      title={`Mediciones · ${nombre}`}
      description={
        producto
          ? `Tanque #${producto.tanque_id} · máximo ${numero(maximo)} ${producto.unidad ?? ''}`.trim()
          : 'Últimas tomas del RIC'
      }
      footer={
        <p className="flex items-center gap-2 text-xs text-ink-500">
          <History className="size-3.5" aria-hidden="true" />
          Se muestran las últimas 12 tomas del tanque.
        </p>
      }
    >
      {consulta.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron cargar las mediciones">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {datos && datos.mediciones.length === 0 ? (
        <Alert variant="info" title="Sin mediciones">
          <span className={ICONO_EN_ALERTA}>
            <Info className="size-4" aria-hidden="true" />
            Este tanque todavía no tiene tomas de RIC registradas.
          </span>
        </Alert>
      ) : null}

      {datos && datos.mediciones.length > 0 && producto ? (
        <div className="space-y-5">
          <BarChart
            labels={vigentes.map((medicion) => fechaCorta(medicion.stamptime))}
            series={[
              {
                label: 'Físico (varillaje)',
                data: vigentes.map((medicion) => medicion.fisico),
                color: producto.color ?? undefined,
              },
              {
                label: 'Capacidad máxima',
                data: vigentes.map(() => maximo),
              },
            ]}
            format="gallons"
            height={200}
          />

          <div className="max-h-[42vh] overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-3 py-2">Toma</th>
                  <th className="px-3 py-2 text-right">Anterior</th>
                  <th className="px-3 py-2 text-right">Ingresos</th>
                  <th className="px-3 py-2 text-right">Salidas</th>
                  <th className="px-3 py-2 text-right">Lógico</th>
                  <th className="px-3 py-2 text-right">Físico</th>
                  <th className="px-3 py-2 text-right">Diferencia</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">% TQ</th>
                  <th className="px-3 py-2">Ejecutor</th>
                </tr>
              </thead>

              <tbody>
                {datos.mediciones.map((medicion) => (
                  <tr key={medicion.id} className="border-t border-ink-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <p className="font-semibold text-ink-800">
                        {fechaCompleta(medicion.stamptime)}
                      </p>
                      <p className="text-xs text-ink-500">
                        {medicion.dias === 0 ? 'hoy' : `hace ${medicion.dias} días`}
                        {medicion.comentario ? ` · ${medicion.comentario}` : ''}
                        {medicion.vigente ? '' : ' · ANULADA'}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {numero(medicion.anterior)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-700">
                      {medicion.ingresos > 0 ? `+${numero(medicion.ingresos)}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-rose-700">
                      {medicion.salidas > 0 ? `−${numero(medicion.salidas)}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {numero(medicion.logico)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      {numero(medicion.fisico)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <span
                        className={
                          medicion.diferencia === 0
                            ? 'text-ink-500'
                            : medicion.diferencia > 0
                              ? 'text-emerald-700'
                              : 'text-rose-700'
                        }
                      >
                        {numero(medicion.diferencia)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {medicion.porcentaje === null ? '—' : `${medicion.porcentaje}%`}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-600">
                      {medicion.ejecutor || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
            <Badge variant="neutral">
              <Scale className="mr-1 size-3.5" aria-hidden="true" />
              Máximo {numero(maximo)}
            </Badge>
            <Badge variant="neutral">Mínimo {numero(producto.capacidad_minima_tanque)}</Badge>
            <span>La diferencia es físico − lógico.</span>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
